import { useCallback, useEffect } from 'react';
import { AsyncThunk } from '@reduxjs/toolkit';
import { usePromise } from 'common/hooks/usePromise';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { useNotifications } from 'store/layout/hooks/useNotifications';

import { getNotificationSettingsSummary } from './notificationSelectors';
import {
  getNotificationSettingsSummaryAsync,
  updateFamilyMemberNotificationSettingsAsync,
  updateFamilyMemberNotificationTopicSettingsAsync,
  updateFamilyNotificationSettingsAsync,
  updateSelfNotificationSettingsAsync,
  updateSelfNotificationTopicSettingsAsync,
} from './notificationsSlice';

function useHandleErrorWrapper<Returned, ThunkArg>(thunkAction: AsyncThunk<Returned, ThunkArg, any>, eventName: string) {
  const dispatch = useAppDispatch();
  const { toastGenericError } = useNotifications();
  const scopePromise = usePromise();

  return (arg: ThunkArg) => {
    return scopePromise(dispatch(thunkAction(arg)).unwrap()).catch((error) => {
      toastGenericError(error, eventName);
    });
  };
}

export const useNotificationSettingsSummary = () => {
  const dispatch = useAppDispatch();
  const notificationSettingsSummary = useAppSelector(({ notifications }) => getNotificationSettingsSummary(notifications));
  const { notificationSettingsSummaryLoadStatus, notificationSettingsSummaryLoadError } = useAppSelector(
    ({ notifications }) => notifications
  );

  useEffect(() => {
    void dispatch(getNotificationSettingsSummaryAsync());
  }, [dispatch]);

  const forceReloadSettings = useCallback(() => {
    void dispatch(getNotificationSettingsSummaryAsync.forceRequest());
  }, [dispatch]);

  return {
    notificationSettingsSummary,
    notificationSettingsSummaryLoadStatus,
    notificationSettingsSummaryLoadError,
    forceReloadSettings,
  };
};

export const useNotificationsDispatch = () => {
  const updateSelfNotificationSettings = useHandleErrorWrapper(updateSelfNotificationSettingsAsync, 'updateSelfNotificationSettings');
  const updateSelfNotificationTopicSettings = useHandleErrorWrapper(
    updateSelfNotificationTopicSettingsAsync,
    'updateSelfNotificationTopicSettings'
  );
  const updateFamilyNotificationSettings = useHandleErrorWrapper(
    updateFamilyNotificationSettingsAsync,
    'updateFamilyNotificationSettings'
  );
  const updateFamilyMemberNotificationSettings = useHandleErrorWrapper(
    updateFamilyMemberNotificationSettingsAsync,
    'updateFamilyMemberNotificationSettings'
  );
  const updateFamilyMemberNotificationTopicSettings = useHandleErrorWrapper(
    updateFamilyMemberNotificationTopicSettingsAsync,
    'updateFamilyMemberNotificationTopicSettings'
  );

  return {
    updateSelfNotificationSettings,
    updateSelfNotificationTopicSettings,
    updateFamilyNotificationSettings,
    updateFamilyMemberNotificationSettings,
    updateFamilyMemberNotificationTopicSettings,
  };
};
